import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { listProjectDomains, type CustomDomainStatus } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'

/**
 * La pantalla de gracias de una compra de dominio.
 *
 * Cobrar y registrar no pasan a la vez: Stripe confirma el pago al navegador en
 * el acto y el registro lo hace el webhook unos segundos después. Sin nada que
 * enseñar en medio, quien acaba de pagar vuelve a una lista donde su dominio
 * todavía no está —que es exactamente lo que parece un cobro perdido—.
 *
 * Igual que la de los planes, esta pantalla no decide nada: sondea hasta que la
 * fila deja de estar en `purchasing` y sólo cuenta lo que ya pasó. Si el sondeo
 * se agota, el dominio sigue siendo suyo: el webhook no depende de que esta
 * pestaña siga abierta.
 */

/** Suficiente para un webhook que llegue tarde, sin quedarse colgado si falla. */
const POLL_ATTEMPTS = 10
const POLL_INTERVAL_MS = 2000

type Phase = 'registering' | 'registered' | 'failed' | 'slow'

export function DomainPurchaseSuccess({
  token,
  projectId,
  domain,
  onDone,
}: {
  token: string
  projectId: string
  domain: string
  /** Cierra la pantalla y devuelve a la lista de dominios, ya recargada. */
  onDone: () => void
}) {
  const { t } = useI18n()
  const [phase, setPhase] = useState<Phase>('registering')

  useEffect(() => {
    let alive = true
    let attempts = 0

    const check = async () => {
      try {
        const { domains } = await listProjectDomains(token, projectId)
        if (!alive) return
        const row = domains.find((d) => d.hostname === domain)
        const status: CustomDomainStatus | undefined = row?.status
        if (status === 'error') { setPhase('failed'); return }
        if (status && status !== 'purchasing') { setPhase('registered'); return }
      } catch { /* se sigue sondeando: un fallo de red no es una compra fallida */ }
      attempts += 1
      if (!alive) return
      if (attempts >= POLL_ATTEMPTS) { setPhase('slow'); return }
      window.setTimeout(() => void check(), POLL_INTERVAL_MS)
    }
    void check()
    return () => { alive = false }
  }, [token, projectId, domain])

  const title = phase === 'registered' ? t('domainSuccess.titleRegistered', { domain })
    : phase === 'failed' ? t('domainSuccess.titleFailed')
    : t('domainSuccess.titleThanks')

  const lead = phase === 'registered' ? t('domainSuccess.leadRegistered')
    : phase === 'failed' ? t('domainSuccess.leadFailed')
      : phase === 'slow' ? t('domainSuccess.leadSlow')
        : t('domainSuccess.leadPending', { domain })

  return createPortal(
    <div className="checkout-page checkout-success">
      <div className="checkout-success-card">
        <img className="checkout-brand" src="/veltica.svg" alt="véltica" />

        <div className="checkout-success-mark" aria-hidden="true">{phase === 'failed' ? '!' : '✓'}</div>
        <h1>{title}</h1>
        <p className="checkout-success-lead">{lead}</p>

        <ul className="checkout-success-next">
          <li>{t('domainSuccess.next1')}</li>
          {/* Lo que nos diferencia de comprarlo fuera, y conviene decirlo aquí:
              nadie tiene que pegar registros en el panel de un registrador. */}
          <li>{t('domainSuccess.next2')}</li>
          <li>{t('domainSuccess.next3')}</li>
        </ul>

        <button type="button" className="checkout-success-cta" onClick={onDone}>
          {t(phase === 'registered' ? 'domainSuccess.ctaSee' : 'domainSuccess.ctaBack')}
        </button>
      </div>
    </div>,
    document.body,
  )
}
